import * as Sentry from '@sentry/vue';
import { getAnalytics, getApptimize } from '@/app-instance';

const defaultState = () => ({
  appBanners: [],
  appStoreLink: null,
  appleLoginEnabled: false,
  answerLabelsEnabled: false,
  beta: false,
  bundleIdentifier: null,
  classifications: [],
  dashboardLabels: {},
  dashboardWidgets: {},
  defaultCustomQuizImageAttachmentId: null,
  facebookAppId: null,
  freeTrialHours: null,
  isMasteryApp: false,
  name: '',
  legacyUrl: '',
  loaded: false,
  logInWithGoogle: false,
  logo: '',
  paidTrialHours: null,
  partnerLogo: '',
  playStoreLink: null,
  previewMastery: false,
  pricingType: '',
  rateLimitingCycleCredits: null,
  rateLimitingCycleDay: null,
  showActivatorsOnSignUp: false,
  questionUiV2Enabled: false,
  trialPeriodHours: null, // deprecated, use paidTrialHours instead in future
});

const getters = {
  appBanners: state => state.appBanners,
  appName: state => state.name,
  appLogo: state => state.logo,
  answerLabelsEnabled: state => state.answerLabelsEnabled,
  partnerLogo: state => state.partnerLogo,
  appLoaded: (state, getters, rootState, rootGetters) => {
    if (!state.loaded) {
      return false;
    }

    const hasMenuItems = rootGetters['navigationItems/menuItems'].length > 0;
    if (!hasMenuItems) {
      Sentry.captureMessage('Application attempted load without menu items');
    }

    return hasMenuItems;
  },
  appStoreLink: state => state.appStoreLink,
  beta: state => state.beta,
  preferredModalHeader: state => state.preferredModalHeader,
  preferredModalSubHeader: state => state.preferredModalSubHeader,
  bundleIdentifier: state => state.bundleIdentifier,
  dashboardLabels: state => state.dashboardLabels || {},
  dashboardWidgets: state => state.dashboardWidgets || {},
  defaultCustomQuizImageAttachmentId: state => state.defaultCustomQuizImageAttachmentId || null,
  facebookAppId: state => state.facebookAppId,
  isCertApp: (state, getters, rootState, rootGetters) => rootGetters['certifications/certificationsList'].length > 0,
  legacyUrl: state => {
    let staging = process.env.WEB_ENV === 'staging' ? 'staging-' : '';
    return `${staging}${state.legacyUrl}`;
  },
  logInWithGoogle: state => state.logInWithGoogle,
  appleLoginEnabled: state => state.appleLoginEnabled,
  isMasteryApp: (state, getters, rootState, rootGetters) => {
    if (state.previewMastery) {
      return true;
    }

    const user = rootGetters['session/currentUser'];
    if (user) {
      const legacyClassificationPV = rootGetters['productVars/get']['legacy_classification_ids'];
      const hasLegacyClassifications = legacyClassificationPV?.parsedValue?.length > 0;
      if (hasLegacyClassifications && legacyClassificationPV.parsedValue.includes(user.classificationId)) {
        return false;
      }
    }

    return state.isMasteryApp;
  },
  playStoreLink: state => state.playStoreLink,
  pricingType: state => state.pricingType || 'Subscription',
  rateLimitingCycleCredits: state => state.rateLimitingCycleCredits,
  rateLimitingCycleDay: state => state.rateLimitingCycleDay,
  freeTrialDuration: state => Math.floor((state.freeTrialHours || 0) / 24),
  showActivatorsOnSignUp: state => state.showActivatorsOnSignUp,
  trialDuration: state => Math.floor((state.trialPeriodHours || 0) / 24),
  questionUiV2Enabled: state => state.questionUiV2Enabled,
};

const mutations = {
  setApp(state, app) {
    state.appStoreLink = app.appStoreLink;
    state.appBanners = app.appBanners;
    state.appleLoginEnabled = app.appleLoginEnabled;
    state.answerLabelsEnabled = app.answerLabelsEnabled;
    state.beta = app.beta;
    state.bundleIdentifier = app.bundleIdentifier;
    state.classifications = app.classifications;
    state.dashboardLabels = app.dashboardLabels;
    state.dashboardWidgets = app.dashboardWidgets;
    state.defaultCustomQuizImageAttachmentId = app.defaultCustomQuizImageAttachmentId;
    state.facebookAppId = app.facebookAppId;
    state.freeTrialHours = app.freeTrialHours;
    state.isMasteryApp = app.webIsMastery;
    state.legacyUrl = app.legacyUrl;
    state.loaded = true;
    state.logInWithGoogle = app.logInWithGoogle;
    state.logo = app.logo;
    state.partnerLogo = app.partnerLogo;
    state.name = app.name;
    state.paidTrialHours = app.paidTrialHours;
    state.playStoreLink = app.googlePlayLink;
    state.pricingType = app.pricingType;
    state.rateLimitingCycleCredits = app.rateLimitingCycleCredits;
    state.rateLimitingCycleDay = app.rateLimitingCycleDay;
    state.showActivatorsOnSignUp = app.showActivatorsOnSignUp;
    state.trialPeriodHours = app.trialPeriodHours;
    state.questionUiV2Enabled = app.questionUiV2Enabled;
    state.preferredModalHeader = app.preferredModalHeader;
    state.preferredModalSubHeader = app.preferredModalSubHeader;
  },
  setPreviewMastery(state) {
    state.previewMastery = true;
  },
};

const actions = {
  loadApp({ dispatch, commit, getters, rootGetters }) {
    return rootGetters['session/ajax'].get('/apps').then(response => {
      commit('setApp', response.data.app);
      commit('activation/setActivators', response.data.app.activatorsApps, { root: true });
      commit('aiEvents/set', response.data.app.aiEvents || [], { root: true });
      commit('flashcardStatuses/setFlashcardStatuses', response.data.app.flashcardStatuses, { root: true });
      commit('catalog/set', response.data.app.catalog, { root: true });
      commit('catalog/setPages', (response.data.app.catalogPages || []), { root: true });
      commit('categoryStatuses/setCategoryStatuses', response.data.app.categoryStatuses, { root: true });
      commit('categoryTypes/setCategoryTypes', response.data.app.categoryTypes, { root: true });
      commit('categories/setCategories', response.data.app.categories, { root: true });
      commit('checklists/set', response.data.app.checklists, { root: true });
      commit('classifications/set', response.data.app.classifications, { root: true });
      commit('contentPartners/set', response.data.app.appContentPartners || [], { root: true });
      commit('contentSources/set', response.data.app.contentSources || [], { root: true });
      commit('dashboards/set', response.data.app.appDashboards, { root: true });
      commit('dashboardWidgets/set', response.data.app.widgets, { root: true });
      commit('decks/set', response.data.app.decks || [], { root: true });
      commit('difficultyLevels/set', response.data.app.difficultyLevels || [], { root: true });
      commit('groupings/set', response.data.app.groupings, { root: true });
      commit('certifications/setCertifications', response.data.app.certifications, { root: true });
      commit('navigationGroups/setNavigationGroups', response.data.app.navigationGroups, { root: true });
      commit('navigationItems/setNavigationItems', response.data.app.navigationItems, { root: true });
      commit('navigationItems/setDefaultNavigationItem', response.data.app.defaultNavigationItemId, { root: true });
      commit('productAssociations/set', response.data.app.productAssociations, { root: true });
      commit('productVars/set', response.data.app.productVars, { root: true });
      commit('purchaseOrders/set', response.data.app.purchaseOrders, { root: true });
      commit('questionnaires/set', response.data.app.questionnaires || [], { root: true });
      commit('simulationConfigs/setSimulationConfigs', response.data.app.simulationConfigs, { root: true });
      commit('tags/set', response.data.app.tags || [], { root: true });
      commit('tags/setLibraryTaggings', response.data.app.libraryTaggings || [], { root: true });
      commit('userProfileConfigs/set', response.data.app.userProfileConfigurations, { root: true });
      const analytics = getAnalytics();
      if (analytics) {
        try {
          analytics.init(rootGetters, response.data.app.analytics);
        } catch (error) {
          // Log analytics initialization failures without breaking app load
          Sentry.captureException(error);

          console.error('Failed to initialize analytics', error);
        }
      } else {
        // Log unexpected absence of analytics during app initialization
        Sentry.captureMessage('Analytics service unavailable during app initialization', {
          level: 'warning',
        });

        console.warn('Analytics service unavailable during app initialization');
      }
      const apptimize = getApptimize();
      if (apptimize) {
        try {
          apptimize.init(rootGetters['productVars/get']['apptimizeWebKey']);
        } catch (error) {
          // Log Apptimize initialization failures without breaking app load
          Sentry.captureException(error);

          console.error('Failed to initialize Apptimize', error);
        }
      } else {
        // Log unexpected absence of Apptimize during app initialization
        Sentry.captureMessage('Apptimize service unavailable during app initialization', {
          level: 'warning',
        });

        console.warn('Apptimize service unavailable during app initialization');
      }

      // Initialize Facebook Login
      if (getters.facebookAppId) dispatch('initFacebookLogin');

      return response;
    });
  },
  initFacebookLogin({ dispatch, commit, getters, rootGetters }) {
    // setup async scenario
    window.fbAsyncInit = function() {
      FB.init({
        appId      : getters.facebookAppId,
        cookie     : true,
        xfbml      : true,
        version    : 'v2.8',
      });
    };

    (function(d, s, id) {
      var js, fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) return;
      js = d.createElement(s); js.id = id;
      js.src = 'https://connect.facebook.net/en_US/sdk.js';
      if (fjs) fjs.parentNode.insertBefore(js, fjs);
    }(document, 'script', 'facebook-jssdk'));
  },
  setAppToMasteryPreview({ commit }) {
    commit('setPreviewMastery', true);
  },
};

export default {
  namespaced: true,
  state: defaultState,
  getters,
  mutations,
  actions,
};
